import React from 'react';
import Paper from '@material-ui/core/Paper';
import { t } from 'ttag';
import useReactRouter from 'use-react-router';
import { useMutation } from '@apollo/react-hooks';
import { MTableToolbar } from 'material-table';
import IconButton from '@material-ui/core/IconButton';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import { Tooltip } from '@material-ui/core';
import DataTable from '../../../components/DataTable';
import { STATUS_STATEMENT_TITLE, USER_TYPES } from '../../../utils/enums';
import useApi from '../../../api';
import useDataList from '../../../hooks/useDataList';
import useUser from '../../../auth/hooks';
import { isIntersection } from '../../../utils/helpers';
import { useNavigator } from '../../../components/Navigator';
import { extractDataError } from '../../../utils/errors';
import useNotifier from '../../../components/Messages/hooks';
import router from '../../../api/router';
import useEditForm from '../../../hooks/useEditForm';
import Loading from '../../../components/Loading';

const Statement = () => {
  const navigate = useNavigator();
  const { user } = useUser();
  const { match } = useReactRouter();
  const certificationsId = match.params.id;
  const api = useApi();
  const pushMessage = useNotifier();
  const [processLoading, setLoading] = React.useState(false);
  const { data, loading, onDeleteStatement, refetch } = useDataList({
    schema: api.schema.statement,
    router: null,
    variables: {
      inputFilter: { certificationsId: Number(certificationsId) },
    },
  });
  const { dataCert, loading: certLoading } = useEditForm({
    schema: api.schema.certifications,
    router: null,
    variables: { inputFilter: { id: Number(certificationsId) } },
  });
  const columns = [
    { title: t`Id`, field: 'trainerId', editable: 'never' },
    {
      title: t`Імя тренера`,
      field: 'trainer_name',
      render: ({ trainer }) => (trainer && trainer.fullName) || '',
    },
    { title: t`Спортсменів`, field: 'countAthletes' },
    { title: t`Екзаменів`, field: 'athleteInExamCount' },
    {
      title: t`Статус`,
      field: 'status',
      render: ({ statusStatement }) => STATUS_STATEMENT_TITLE[statusStatement],
    },
  ];
  const [closeStatement] = useMutation(
    api.schema.certifications_athlete.close()[0],
    {
      onCompleted: (d) => {
        const { closeStatement: res } = d;
        if (res.ok) {
          pushMessage({ text: 'Відомість закрита' });
          refetch();
        } else {
          const dataError = extractDataError(res.errors);
          pushMessage({ text: dataError });
        }
      },
    }
  );
  const [openStatement] = useMutation(
    api.schema.certifications_athlete.open()[0],
    {
      onCompleted: (d) => {
        const { openStatement: res } = d;
        if (res.ok) {
          pushMessage({ text: 'Відомість відкрита' });
          refetch();
        } else {
          const dataError = extractDataError(res.errors);
          pushMessage({ text: dataError });
        }
      },
    }
  );

  const [generateProfileInfo] = useMutation(
    api.schema.certifications.generateProfileInfo()[0],
    {
      onCompleted: (d) => {
        setLoading(false);
        const res = d[api.schema.certifications.generateProfileInfo()[1]];
        if (res.ok) {
          window.open(res.url, '_blank').focus();
        }
      },
    }
  );

  const handleGenPdf = (e, rowData) => {
    setLoading(true);
    generateProfileInfo({
      variables: {
        id: certificationsId,
        trainerId: rowData.trainer.id,
      },
    });
  };
  if (loading || certLoading) return <Loading />;
  return (
    <Paper>
      <DataTable
        isLoading={loading}
        processLoading={processLoading}
        title='Відомості'
        columns={columns}
        data={data || []}
        components={{
          Toolbar: (props) => (
            <div>
              <MTableToolbar {...props} />
              <div style={{ padding: '0px 10px' }}>
                <Tooltip title='Повернутись до загального списку екзамену'>
                  <IconButton
                    aria-label='delete'
                    onClick={
                      user.isAdmin
                        ? () =>
                            navigate(
                              router.admin.certifications.athleteList(
                                certificationsId
                              )
                            )
                        : () =>
                            navigate(
                              router.cabinet.certifications.athleteList(
                                certificationsId
                              )
                            )
                    }
                  >
                    <KeyboardBackspaceIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          ),
        }}
        onGenerateRowPdf={handleGenPdf}
        onOpenStatement={
          isIntersection(user.types, [
            USER_TYPES.superManager,
            USER_TYPES.manager,
          ]) || user.isAdmin
            ? (event, rowData) =>
                openStatement({
                  variables: {
                    id: certificationsId,
                    trainerId: rowData.trainer.id,
                  },
                })
            : null
        }
        onCloseStatement={
          isIntersection(user.types, [
            USER_TYPES.superManager,
            USER_TYPES.manager,
          ]) || user.isAdmin
            ? (event, rowData) =>
                closeStatement({
                  variables: {
                    id: certificationsId,
                    trainerId: rowData.trainer.id,
                  },
                })
            : null
        }
        onViewAthlete={(event, rowData) =>
          navigate(
            api.router.certifications.ListAthlete(
              certificationsId,
              rowData.trainer.id
            )
          )
        }
        onDelete={
          isIntersection(user.types, [USER_TYPES.trainer])
            ? null
            : onDeleteStatement
        }
        isDeletableFn={() => !(dataCert.status == 2)}
      />
    </Paper>
  );
};

export default Statement;
